import { $axios } from 'boot/axios';

const defaultState = () => ({
  book: undefined,
  form: {
    templateType: 'booksbook',
  },
  bookTemplate: undefined,
  bookGeneration: undefined,
  chapterTemplate: undefined,
  chaptersGenerations: {},
  currentPanel: 0,
  coverImages: [],
});

const imageGenerationTypes = {
  drawn: ['ai-dalle', 'ai-stable-diffusion'],
  photo: ['pixabay', 'unsplash'],
};
const transformFormToModel = (fieldsSchema, form) => {
  const result = { data: { meta: {} } };
  Object.keys(fieldsSchema).forEach((item) => {
    if (fieldsSchema[item].isMeta === false) {
      result.data[item] = form[item];
    } else {
      result.data.meta[item] = form[item];
    }
  });
  result.data.templateType = form.templateType;
  result.documentsCount = 1;
  return result;
};

const actions = {
  async updateBookImage({ state, commit }, { url, guid }) {
    const { data } = await $axios.put(`generator/books/${state.book.guid}`, {
      ...state.book,
      coverImageUrl: url,
      metaFields: {
        ...state.book.metaFields,
        coverImageFileGuid: guid,
      },
    }, {
      skipReplaceInterceptor: true,
    });
    commit('setBook', {
      ...state.book,
      coverImageUrl: data.data.coverImageUrl,
    });
  },
  async createBook({ commit, state }) {
    const { data } = await $axios.post('generator/books', {
      title: state.form.title,
    });
    commit('setBook', data.data);
    return data.data;
  },
  async fetchBook({ commit }, { guid }) {
    const { data } = await $axios.get(`generator/books/${guid}`);
    commit('setBook', data.data);
    return data.data;
  },
  async createBookGeneration({ state, commit }, { isEmptyBook }) {
    const payload = transformFormToModel(state.bookTemplate.fieldsSchema, state.form);
    if (isEmptyBook) {
      payload.immediatelyStart = false;
    }
    const { data } = await $axios.post('generator/generations', {
      ...payload,
      bookGuid: state.book.guid,
    });
    commit('setBookGeneration', data.data);
    return data.data;
  },
  async fetchBookGeneration({ commit }, { guid }) {
    const { data } = await $axios.get(`generator/generations/${guid}`);
    commit('setBookGeneration', data.data);
    return data.data;
  },
  async createChapterGeneration({ commit, state }, chapter) {
    const formEntries = Object.entries(state.form).map(([key, value]) => {
      if (key === 'title') { return ['bookTitle', value]; }
      if (key === 'description') { return ['bookDescription', value]; }
      if (key === 'brandVoice') { return ['brandVoice', value?.guid]; }
      return [key, value];
    });
    const payload = transformFormToModel(
      state.chapterTemplate.fieldsSchema,
      {
        ...Object.fromEntries(formEntries),
        ...chapter,
      },
    );
    payload.data.templateType = 'bookschapter';

    const { data } = await $axios.post('generator/generations', {
      ...payload,
      bookChapterGuid: chapter.guid,
      immediatelyStart: chapter.position === 0,
    });
    commit('setChaptersGenerationsGeneration', data.data);
    return data.data;
  },
  async fetchBookTemplate({ commit }) {
    const { data } = await $axios.get('generator/templates/booksbook');
    commit('setBookTemplate', data.data);
  },
  async fetchChapterTemplate({ commit }) {
    const { data } = await $axios.get('generator/templates/bookschapter');
    commit('setChapterTemplate', data.data);
  },
  async deleteChapter({ commit, state }, guid) {
    await $axios.delete(`generator/books/${state.book.guid}/chapters/${guid}`);

    commit('setBook', {
      ...state.book,
      chapters: state.book.chapters.filter((item) => item.guid !== guid).map((item, index) => ({ ...item, position: index })),
    });
  },
  async createChapter({ dispatch, state }, chapter) {
    const { data } = await $axios.post(`generator/books/${state.book.guid}/chapters/`, chapter);
    dispatch('fetchBook', { guid: state.book.guid });
    return data.data;
  },
  async updateChapterPosition({ commit, state }, { chapter, position }) {
    await $axios.put(`generator/books/${state.book.guid}/chapters/${chapter.guid}`, {
      ...chapter,
      position,
    });
    const { data: bookData } = await $axios.get(`generator/books/${state.book.guid}`);
    commit('setBook', bookData.data);
    const newOrder = bookData.data.chapters.reduce((accumulator, value) => ({ ...accumulator, [value.guid]: value }), {});
    const newChapters = state.book.chapters.map((value) => ({ ...value, position: newOrder[value.guid].position })).toSorted((a, b) => a.position - b.position);

    commit('setBookChapters', newChapters);
  },
  async updateChapterTitle({ commit, state }, { chapter, title }) {
    const { data } = await $axios.put(`generator/books/${state.book.guid}/chapters/${chapter.guid}`, {
      ...chapter,
      title,
    });
    commit('setChapterTitle', { i: data.data.position, payload: data.data.title });
  },
  findImagesByType({ commit }, { description, type = 'drawn' }) {
    imageGenerationTypes[type].forEach((imageLibrary) => {
      $axios.post('files/images/find', {
        description,
        sourceType: imageLibrary,
      }).then(({ data }) => {
        commit('setCoverImages', data.data);
      });
    });
  },
};

const mutations = {
  setCoverImages(state, payload) {
    state.coverImages = [...state.coverImages, ...payload];
  },
  setBook(state, payload) {
    state.book = payload;
  },
  setBookGeneration(state, payload) {
    state.bookGeneration = payload;
  },
  setCurrentPanel(state, payload) {
    state.currentPanel = payload;
  },
  setFormField(state, { scope, payload }) {
    state.form[scope] = payload;
  },
  setBookTemplate(state, payload) {
    state.bookTemplate = payload;
  },
  setChaptersGenerationsGeneration(state, chapter) {
    state.chaptersGenerations[chapter.guid] = chapter;
  },
  setFormFromGenerationByTemplate(state) {
    const { fieldsSchema } = state.bookTemplate;
    Object.keys(fieldsSchema).forEach((fieldKey) => {
      if (fieldsSchema[fieldKey].isMeta === false) {
        state.form[fieldKey] = state.bookGeneration[fieldKey];
      } else {
        state.form[fieldKey] = state.bookGeneration.meta[fieldKey];
      }
    });
  },
  setChapterTemplate(state, payload) {
    state.chapterTemplate = payload;
  },
  setBookChaptersNote(state, { i, payload }) {
    state.book.chapters = state.book.chapters.map((it, index) => (i !== index ? it : { ...it, note: payload }));
  },
  setChapterTitle(state, { i, payload }) {
    state.book.chapters = state.book.chapters.map((it, index) => (i !== index ? it : { ...it, title: payload }));
  },
  setBookChapters(state, payload) {
    state.book.chapters = payload;
  },
  clear(state) {
    const clearState = defaultState();
    Object.keys(clearState).forEach((key) => {
      state[key] = clearState[key];
    });
  },
};

export default {
  namespaced: true,
  mutations,
  actions,
  state: defaultState,
};
